/**
 * Submit-path facade over the chat turn engine (HIG-5971). This hook only
 * wires React state into the flow: the submit orchestration lives in
 * `submit-flow.ts`, and the event loop, accumulators, and store writes live
 * in `chat/engine/` (`submit.ts` → `turn-pipeline.ts` → `accumulator.ts`).
 */
import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAllChatMessages,
  useMessagesStore,
  useSetActiveChat,
  useActiveChatId,
  useSetChatInput,
  useSetPendingMessage,
  useFinalizeAttachments,
  useAttachments,
  useAutoMemoryCollection,
  useAddChatIdMapping,
  getCurrentChatInput,
} from '../store'
// Imported from the leaf module, not the `../store` barrel: `submitQueue` below
// reads these at module scope, and the barrel is still mid-evaluation here
// (store/messages → ../hooks → this file), so barrel bindings are TDZ-dead.
import {
  getQueueForChat,
  enqueueMessage,
  hydrateQueuedMessage,
  removeQueuedMessage,
  isQueuedMessagePresent,
  setQueuedAttachments,
  markChatAwaitingNextDrain,
  clearChatAwaitingNextDrain,
} from '../store/queue'
import { useActions } from '../providers'
import { useMessageMentions, useResetMentions, useValidateMentions, useMessageListScrollActions } from '../components'
import { useModels } from './useModels'
import { useInvalidateHistoryPreview, useInvalidateChatHistory } from './chat-history'
import { useContextToAttachments } from './useContextToAttachments'
import { useResetChatContext } from '../store/atoms'
import { useClearTurnError, useSetTurnError } from '../store/turn-error'
import { useConfirmServerChatId, useTrackLocalChatId } from '../store/local-chat-ids'
import { createSubmitFlow, type SubmitMessage } from './submit-flow'
import type { SubmitQueue } from './submit-presentation'
import { useCreateAutoToolTracker } from './use-auto-tool-responses'
import { useEngineDeps } from './use-engine-deps'
import { useModelChoice } from './use-plan-features'
import { useRequestedReasoningEffort } from './use-reasoning-effort'
import { useTakePreparedConversationId } from './use-prepare-turn'
import { useTakeNewChatSandboxId } from '../store/local-paths'
import { messagesQueryKey } from './messages-query'
import { restartOnboarding } from '@renderer/windows/features/onboarding/restart-onboarding'

// The flow's view of the dashed-bubble queue, bound to the module-level store.
const submitQueue: SubmitQueue = {
  getForChat: getQueueForChat,
  enqueue: enqueueMessage,
  hydrate: hydrateQueuedMessage,
  remove: removeQueuedMessage,
  isPresent: isQueuedMessagePresent,
  setAttachments: setQueuedAttachments,
  markAwaitingNextDrain: markChatAwaitingNextDrain,
  clearAwaitingNextDrain: clearChatAwaitingNextDrain,
}

// Re-exported through the barrel for callers that historically imported these
// from this module.
export { useSubmitChatRequest, type ChatRequestBody } from './use-submit-chat-request'
export { useSubmitToolResponse } from './use-submit-tool-response'

export const useSubmitMessage = (): SubmitMessage => {
  const engine = useEngineDeps()
  const activeChatId = useActiveChatId()
  const setActiveChatId = useSetActiveChat()
  const { appendMessage, updateLastMessage, removeLastMessage, updateMessagesChatId, removeUnadoptedPlaceholder } =
    useMessagesStore()
  const { onChunkParsed, requestCurrentContext, getActiveDisplayScreenshot } = useActions()
  const setPendingMessage = useSetPendingMessage()
  const messageMentions = useMessageMentions()
  const validateMentions = useValidateMentions()
  const resetMentions = useResetMentions()
  const setChatInput = useSetChatInput()
  const allChatMessages = useAllChatMessages()
  const invalidateHistoryPreview = useInvalidateHistoryPreview()
  const invalidateChatHistory = useInvalidateChatHistory()
  const addChatIdMapping = useAddChatIdMapping()
  const attachments = useAttachments()
  const contextToAttachments = useContextToAttachments()
  const finalizeAttachments = useFinalizeAttachments()
  const resetChatContext = useResetChatContext()
  // Use the models hook with refreshBeforeRequest enabled
  const { refreshDefaultModel } = useModels({ refreshBeforeRequest: true })
  const { scrollToEnd } = useMessageListScrollActions()
  const autoMemoryCollection = useAutoMemoryCollection()
  const reasoningEffort = useRequestedReasoningEffort()
  const modelChoice = useModelChoice()
  const setTurnError = useSetTurnError()
  const clearTurnError = useClearTurnError()
  const trackLocalChatId = useTrackLocalChatId()
  const confirmServerChatId = useConfirmServerChatId()
  const createAutoToolTracker = useCreateAutoToolTracker()
  const takePreparedConversationId = useTakePreparedConversationId()
  const takeNewChatSandboxId = useTakeNewChatSandboxId()
  const queryClient = useQueryClient()

  // Latest transcript snapshot in a ref so the flow's commit-time reads see
  // current messages — the queue path commits long after this render.
  const allChatMessagesRef = useRef(allChatMessages)
  useEffect(() => {
    allChatMessagesRef.current = allChatMessages
  }, [allChatMessages])

  const isExistingChat = useMemo(() => {
    if (activeChatId) {
      const hasAssistantMessage = allChatMessages[activeChatId]?.some((msg) => msg.role === 'assistant')
      if (hasAssistantMessage) {
        return true
      }
    }
    return false
  }, [activeChatId, allChatMessages])

  return createSubmitFlow({
    engine,
    queue: submitQueue,
    snapshot: {
      activeChatId,
      isExistingChat,
      allChatMessages,
      attachments,
      messageMentions,
      autoMemoryCollection,
      modelChoice,
      reasoningEffort,
    },
    live: {
      getCurrentChatInput,
      getChatMessages: (chatId) => allChatMessagesRef.current[chatId],
    },
    store: {
      appendMessage,
      updateLastMessage,
      removeLastMessage,
      updateMessagesChatId,
      removeUnadoptedPlaceholder,
      setPendingMessage,
      setChatInput,
      setActiveChatId,
      setTurnError,
      clearTurnError,
      trackLocalChatId,
      confirmServerChatId,
      takePreparedConversationId,
      takeNewChatSandboxId,
    },
    ui: {
      validateMentions,
      resetMentions,
      scrollToEnd,
      restartOnboarding,
    },
    data: {
      refreshDefaultModel,
      invalidateHistoryPreview,
      invalidateChatHistory,
      reconcileChatMessages: async (chatId) => {
        await queryClient.invalidateQueries({ queryKey: messagesQueryKey(chatId) })
      },
      addChatIdMapping,
      finalizeAttachments,
      contextToAttachments,
      resetChatContext,
    },
    actions: {
      onChunkParsed,
      requestCurrentContext,
      getActiveDisplayScreenshot,
      createAutoToolTracker,
    },
  })
}
